// ABOUTME: Mount/unmount lifecycle for an overlay that fades instead of popping — the entry and
// ABOUTME: exit halves of PointTagLayer's dwell machine, extracted for overlays with no layer above.

import { useEffect, useRef, useState } from 'react';

/** Matches PointTag's own `transition: opacity 400ms ease` — the chrome fades at one speed. */
export const FADE_MS = 400;

/**
 * Holds a component at opacity 0 for one paint after its gate opens, and keeps it mounted for one
 * fade after the gate closes.
 *
 * Both halves exist because CSS cannot transition an element that was never painted at the starting
 * value: mounting straight at `opacity: 1` gives the browser nothing to ease FROM, and unmounting on
 * the falling edge takes the element away before it can ease back. `PointTagLayer` already solves
 * this for the point tags with a phase machine of its own — this is the same behaviour for the
 * overlays that have no layer above them driving it (client, 2026-09-01: the spec sheet "didn't
 * fade in/out as all other tag UIs", because it returns null the instant its gate closes).
 *
 * TWO animation frames before the opacity flip, not a timeout. PointTagLayer records why: a bare
 * timeout raced the first paint under decode/blur load and popped the plate on instead of easing it
 * in. The first frame can still be scheduled before the opacity-0 render commits; the second is the
 * one guaranteed to run after it.
 *
 * Initial state follows `visible` rather than always starting hidden, so a component that is ALREADY
 * open on its first render paints at full opacity — the `__visual__` plate harness serialises a
 * single render with no frames in between, and a hook that always started at 0 would write blank
 * plates. Only transitions animate, which is also what the real gate does: SpecSheetOverlay stays
 * mounted across a chapter change and flips `visible`, so first render is always the closed state.
 *
 * @param {boolean} visible the caller's own gate
 * @param {number} durationMs must match the CSS transition the caller applies
 * @returns {{ present: boolean, opacity: number }} `present` false means it is safe to return null
 */
export function useFadePresence(visible, durationMs = FADE_MS) {
  const [present, setPresent] = useState(visible);
  const [opaque, setOpaque] = useState(visible);
  const frameRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (visible) {
      // Cancelling the pending unmount is the cleanup's job (below), so a gate that closes and
      // reopens inside one fade eases back in from wherever it got to rather than popping.
      setPresent(true);
      if (typeof requestAnimationFrame !== 'function') { setOpaque(true); return undefined; }
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = requestAnimationFrame(() => setOpaque(true));
      });
      return () => cancelAnimationFrame(frameRef.current);
    }
    setOpaque(false);
    timerRef.current = setTimeout(() => setPresent(false), durationMs);
    return () => clearTimeout(timerRef.current);
  }, [visible, durationMs]);

  return { present, opacity: opaque ? 1 : 0 };
}

export default useFadePresence;
